interface DataPoint {
  label: string;
  value: number;
}

interface SvgLineChartProps {
  data: DataPoint[];
  width?: number;
  height?: number;
  color?: string;
  label?: string;
}

const PAD_LEFT = 48;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 36;

export function SvgLineChart({
  data,
  width = 600,
  height = 200,
  color = "var(--chart-series-1)",
  label,
}: SvgLineChartProps) {
  const chartW = width - PAD_LEFT - PAD_RIGHT;
  const chartH = height - PAD_TOP - PAD_BOTTOM;

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--color-text-muted)",
          fontSize: "0.85rem",
          background: "var(--color-bg-secondary)",
          borderRadius: 6,
          border: "1px solid var(--color-border)",
        }}
      >
        No data available
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => d.value), 1);
  const minVal = 0;
  const range = maxVal - minVal || 1;

  const toX = (i: number) =>
    PAD_LEFT + (data.length === 1 ? chartW / 2 : (i / (data.length - 1)) * chartW);

  const toY = (v: number) =>
    PAD_TOP + chartH - ((v - minVal) / range) * chartH;

  // Grid lines (5 lines)
  const gridLines = Array.from({ length: 5 }, (_, i) => {
    const val = minVal + (range / 4) * i;
    const y = toY(val);
    return { y, label: Math.round(val).toString() };
  });

  // X-axis labels: up to 5 evenly spaced
  const xLabelCount = Math.min(5, data.length);
  const xLabelIndices = Array.from({ length: xLabelCount }, (_, i) =>
    Math.round((i / (xLabelCount - 1 || 1)) * (data.length - 1))
  );
  // Dedupe in case of 1 item
  const uniqueXIndices = [...new Set(xLabelIndices)];

  // Build SVG path
  const linePath = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${toX(i).toFixed(1)} ${toY(d.value).toFixed(1)}`)
    .join(" ");

  // Area fill path
  const areaPath =
    linePath +
    ` L ${toX(data.length - 1).toFixed(1)} ${(PAD_TOP + chartH).toFixed(1)}` +
    ` L ${toX(0).toFixed(1)} ${(PAD_TOP + chartH).toFixed(1)} Z`;

  return (
    <div style={{ width: "100%" }}>
      {label && (
        <div
          style={{
            fontSize: "0.75rem",
            color: "var(--color-text-secondary)",
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </div>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines */}
        {gridLines.map((gl, i) => (
          <g key={i}>
            <line
              x1={PAD_LEFT}
              y1={gl.y}
              x2={PAD_LEFT + chartW}
              y2={gl.y}
              style={{ stroke: "var(--color-border)" }}
              strokeWidth="1"
            />
            <text
              x={PAD_LEFT - 6}
              y={gl.y + 4}
              textAnchor="end"
              fontSize="10"
              style={{ fill: "var(--color-text-muted)" }}
            >
              {gl.label}
            </text>
          </g>
        ))}

        {/* Area fill */}
        {data.length > 1 && (
          <path
            d={areaPath}
            style={{ fill: color }}
            fillOpacity="0.08"
          />
        )}

        {/* Line */}
        {data.length > 1 && (
          <path
            d={linePath}
            fill="none"
            style={{ stroke: color }}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Dots */}
        {data.map((d, i) => (
          <circle
            key={i}
            cx={toX(i)}
            cy={toY(d.value)}
            r="3"
            style={{ fill: color }}
          />
        ))}

        {/* X-axis labels */}
        {uniqueXIndices.map((idx) => (
          <text
            key={idx}
            x={toX(idx)}
            y={height - 6}
            textAnchor="middle"
            fontSize="10"
            style={{ fill: "var(--color-text-muted)" }}
          >
            {data[idx]?.label ?? ""}
          </text>
        ))}

        {/* Axes */}
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP}
          x2={PAD_LEFT}
          y2={PAD_TOP + chartH}
          style={{ stroke: "var(--color-border)" }}
          strokeWidth="1"
        />
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP + chartH}
          x2={PAD_LEFT + chartW}
          y2={PAD_TOP + chartH}
          style={{ stroke: "var(--color-border)" }}
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}
